import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/lib/supabase';
import { ApiCallError, callFunction } from '@/lib/api';
import { AuthShell } from '@/pages/auth/AuthShell';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { ErrorAlert } from '@/components/ui/ErrorAlert';

interface AcceptPatientPortalOutput {
  user_id: string;
  email: string;
  patient_id: string;
  clinic_id: string;
  session: {
    access_token: string;
    refresh_token: string;
  };
}

export function PortalAcceptInvitation() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (!token) {
    return (
      <AuthShell
        title={t('portal.accept.title')}
        subtitle={t('portal.accept.subtitle')}
        footer={
          <Link to="/portal/login" className="font-medium text-indigo-600 hover:text-indigo-500">
            {t('portal.accept.back_to_login')}
          </Link>
        }
      >
        <ErrorAlert message={t('portal.accept.missing_token')} />
      </AuthShell>
    );
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);

    if (password.length < 8) {
      setError(t('auth.reset_password.min_length'));
      return;
    }
    if (password !== confirm) {
      setError(t('auth.reset_password.mismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const result = await callFunction<
        { token: string; password: string },
        AcceptPatientPortalOutput
      >('accept-patient-portal', { token, password });

      const { error: setErr } = await supabase.auth.setSession({
        access_token: result.session.access_token,
        refresh_token: result.session.refresh_token,
      });
      if (setErr) throw setErr;

      navigate('/portal/dashboard', { replace: true });
    } catch (err) {
      if (err instanceof ApiCallError) {
        switch (err.code) {
          case 'not-found':
            setError(t('portal.accept.invalid_or_expired'));
            break;
          case 'unauthorized':
            setError(t('portal.accept.existing_user_password'));
            break;
          case 'invalid-input':
            setError(t('portal.accept.bad_input'));
            break;
          default:
            setError(err.message || t('auth.errors.unknown'));
        }
      } else {
        setError(err instanceof Error ? err.message : t('auth.errors.unknown'));
      }
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t('portal.accept.title')}
      subtitle={t('portal.accept.subtitle')}
      footer={
        <Link to="/portal/login" className="font-medium text-indigo-600 hover:text-indigo-500">
          {t('portal.accept.back_to_login')}
        </Link>
      }
    >
      {error && <ErrorAlert message={error} />}
      <form onSubmit={handleSubmit} className="space-y-6">
        <Input
          label={t('portal.accept.password')}
          name="password"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          hint={t('portal.accept.password_hint')}
        />
        <Input
          label={t('portal.accept.password_confirm')}
          name="passwordConfirm"
          type="password"
          autoComplete="new-password"
          required
          minLength={8}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" size="full" disabled={submitting}>
          {submitting ? t('portal.accept.submitting') : t('portal.accept.submit')}
        </Button>
      </form>
    </AuthShell>
  );
}
